import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from './Toast'
import { useRunningTimers } from '../hooks/useRunningTimers'
import { elapsedSeconds, billedAmount, clock, isPaused, pauseTimer, resumeTimer, stopTimer } from '../lib/runningTimers'

const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)

/**
 * The top-bar timer, for however many are running.
 *
 * Collapsed it shows the timer that is actually counting (or the newest, when
 * all are paused) and how many others there are; open, it lists every one with
 * its own pause and stop. Starting timers happens on the Time Tracking page and
 * the project cards, where there is room to pick a client and a rate.
 */
export default function TimerWidget() {
  const navigate = useNavigate()
  const { timers, now } = useRunningTimers()
  const [open, setOpen] = useState(false)
  const [busyId, setBusyId] = useState(null)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  if (!timers.length) {
    return (
      <button onClick={() => navigate('/time')} className="tb-icon"
        style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 10px', background: 'none', border: '1px solid var(--bdr)', fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <svg width="14" height="14" viewBox="0 0 11 11" fill="none" aria-hidden="true">
          <circle cx="5.5" cy="5.5" r="4.5" stroke="currentColor" strokeWidth="1.2"/>
          <path d="M5.5 3v2.5l1.5 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
        Timer
      </button>
    )
  }

  const counting = timers.filter(t => !isPaused(t))
  const lead = counting[counting.length - 1] || timers[timers.length - 1]
  const allPaused = counting.length === 0

  async function act(timer, fn, failMsg) {
    setBusyId(timer.id)
    try { await fn(timer) } catch (err) { toast.error(`${failMsg}: ${err.message}`) } finally { setBusyId(null) }
  }

  const small = { padding: '5px 10px', border: '1px solid var(--bdr2)', background: 'transparent', fontFamily: 'var(--fu)', fontSize: 11, fontWeight: 600, color: 'var(--txt)', cursor: 'pointer' }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'var(--accent-soft)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', padding: '6px 10px 6px 8px', cursor: 'pointer' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: allPaused ? 'var(--warning)' : 'var(--success)', flexShrink: 0, animation: allPaused ? 'none' : 'pulse 1.5s ease-in-out infinite' }} />
        <span style={{ fontFamily: 'var(--fd)', fontSize: 13, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.04em', fontVariantNumeric: 'tabular-nums', minWidth: 40 }}>
          {clock(elapsedSeconds(lead, now))}
        </span>
        {timers.length > 1 && (
          <span style={{ fontFamily: 'var(--fu)', fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: 'var(--card)', border: '1px solid var(--bdr)', padding: '1px 7px' }}>
            {timers.length}
          </span>
        )}
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'none' }}>
          <path d="M1.5 3l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 200, width: 340, maxWidth: 'calc(100vw - 32px)', background: 'var(--card)', border: '1px solid var(--bdr)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
          {timers.map(t => {
            const paused = isPaused(t)
            const amount = billedAmount(t, now)
            const busy = busyId === t.id
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--bdr)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, color: 'var(--txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.clients?.name || 'No client'}{(t.projects?.title || t.project_name) ? ` · ${t.projects?.title || t.project_name}` : ''}
                  </div>
                  <div style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--mut)', fontVariantNumeric: 'tabular-nums' }}>
                    <span style={{ color: paused ? 'var(--warning)' : 'var(--txt)', fontWeight: 600 }}>{clock(elapsedSeconds(t, now))}</span>
                    {paused && ' · paused'}
                    {amount !== null ? ` · ${money(amount)}` : ' · no rate'}
                  </div>
                </div>
                {paused
                  ? <button disabled={busy} onClick={() => act(t, resumeTimer, 'Could not resume')} style={small}>Resume</button>
                  : <button disabled={busy} onClick={() => act(t, pauseTimer, 'Could not pause')} style={small}>Pause</button>}
                <button disabled={busy} onClick={() => act(t, stopTimer, 'Could not save — the timer is still running')} style={{ ...small, color: 'var(--red)', borderColor: 'color-mix(in srgb, var(--red) 40%, transparent)' }}>
                  {busy ? '…' : 'Stop'}
                </button>
              </div>
            )
          })}
          <button onClick={() => { setOpen(false); navigate('/time') }}
            style={{ width: '100%', padding: '10px 12px', background: 'var(--off)', border: 'none', fontFamily: 'var(--fu)', fontSize: 11, fontWeight: 600, color: 'var(--mut)', cursor: 'pointer', textAlign: 'left' }}>
            + Start another timer · edit rates →
          </button>
        </div>
      )}
    </div>
  )
}
